import { useState } from 'react';

import { useNavigate } from '@tanstack/react-router';
import { Modal } from 'cpool-components/general/Modal';
import { Button } from 'cpool-components/input/Button';
import { ButtonVariant } from 'cpool-components/input/Button/types';
import { getActiveOrFirstUserId, getAddedAdvisorId, isLeadCarryingRole, type LeadAssignment } from 'cpool-utils';
import { useTranslation } from 'react-i18next';
import { LuCircleX, LuSave } from 'react-icons/lu';

import { type AdviserOption, AdviserSelect } from './AdviserSelect';
import { getAdviserName } from './getAdviserName';
import { useLeadAdvisers } from './useLeadAdvisers';
import { LEAD_LIST_DEFAULT_FILTERS } from '~/constants';
import { useAdviserAssign } from '~/features/leadList/hooks/useAdviserAssign';
import { useAdviserMutation } from '~/features/leadList/hooks/useAdviserMutation';
import useUserInfo from '~/hooks/useUserInfo';

export type ChangeAdviserLead = LeadAssignment & { id: string };

type Props = {
    lead: ChangeAdviserLead;
    onClose: () => void;
    open: boolean;
    originalLoanRequestId?: string;
};

const LABEL_ID = 'change-adviser-label';

export const ChangeAdviserModal: React.FC<Props> = ({ lead, onClose, open, originalLoanRequestId }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const user = useUserInfo();
    const currentAdviserId = getActiveOrFirstUserId(lead) ?? undefined;
    const { advisers } = useAdviserAssign(currentAdviserId, lead.userIds);
    // same query as useAdviserAssign, but keeps nameOnly users for the current adviser's and added advisor's names
    const { data: allAdvisers = [], isLoading: advisersLoading } = useLeadAdvisers(lead);
    const { changeAdviser, isPending } = useAdviserMutation(originalLoanRequestId ?? lead.id);
    const addedAdvisorId = getAddedAdvisorId(lead);
    const [selectedId, setSelectedId] = useState<string>();
    // advisers never contain the current one, so a pick of it is no change
    const selected = advisers.find(({ id }) => id === selectedId);
    const options: AdviserOption[] = [
        ...(currentAdviserId
            ? [
                  {
                      disabled: true,
                      label: getAdviserName(allAdvisers, currentAdviserId, t('LEADS.ASSIGNED_USERS.UNKNOWN_USER')),
                      value: currentAdviserId,
                  },
              ]
            : []),
        ...advisers.map(({ id, isAssignable, name }) => ({ disabled: !isAssignable, label: name, value: id })),
    ];

    const close = () => {
        setSelectedId(undefined);
        onClose();
    };

    const save = async () => {
        if (!selected) return;
        try {
            await changeAdviser({ adviserId: selected.id, leadId: lead.id, name: selected.name });
        } catch {
            // the mutation's toast explains the failure
            return;
        }
        close();
        if (isLeadCarryingRole(user?.role)) navigate({ search: LEAD_LIST_DEFAULT_FILTERS, to: '/leads' });
    };

    return (
        <Modal onClose={close} open={open} panelClassName="max-w-lg" title={t('LEADS.ASSIGNED_USERS.CHANGE_TITLE')}>
            <div className="flex flex-col gap-6" data-testid="change-adviser-modal">
                <div className="flex flex-col gap-2">
                    <div className="grid grid-cols-[max-content_1fr] items-center gap-x-6">
                        <span className="text-sm text-midnight" id={LABEL_ID}>
                            {t('LEADS.ASSIGNED_USERS.CASE_WORKER')}
                        </span>
                        {/* no unknown-user flash while the names load */}
                        {!advisersLoading && (
                            <AdviserSelect
                                aria-labelledby={LABEL_ID}
                                data-testid="change-adviser-select"
                                disabled={isPending}
                                onSelect={setSelectedId}
                                options={options}
                                placeholder={t('SELECT_PLACEHOLDER')}
                                value={selectedId ?? currentAdviserId}
                            />
                        )}
                    </div>
                    {addedAdvisorId && !advisersLoading && (
                        <span className="text-xs text-grey" data-testid="change-adviser-share-hint">
                            {t('LEADS.ASSIGNED_USERS.SHARE_ENDS_HINT', {
                                name: getAdviserName(
                                    allAdvisers,
                                    addedAdvisorId,
                                    t('LEADS.ASSIGNED_USERS.UNKNOWN_USER'),
                                ),
                            })}
                        </span>
                    )}
                </div>
                <div className="flex justify-end gap-2">
                    <Button
                        disabled={isPending}
                        iconLeft={<LuCircleX />}
                        onClick={close}
                        variant={ButtonVariant.SECONDARY}
                    >
                        {t('BUTTONS.CANCEL')}
                    </Button>
                    <Button
                        data-testid="change-adviser-save"
                        disabled={!selected || isPending}
                        iconLeft={<LuSave />}
                        onClick={save}
                        variant={ButtonVariant.PRIMARY}
                    >
                        {t('BUTTONS.SAVE')}
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
